import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiClient } from '../services/api';
import { X, Save, Calendar, Users, FileText, Tag, Plus, ArrowLeft } from 'lucide-react';
import '../components/EditEventModal.css'; // Reuse styling from EditEventModal
import MarkdownEditor from '../components/MarkdownEditor';

const CreateEvent: React.FC = () => {
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    theme: '',
    status: 'draft' as 'draft' | 'published' | 'ongoing' | 'completed' | 'cancelled',
    startDate: '',
    endDate: '',
    registrationDeadline: '',
    maxParticipants: 100,
    eventLink: '',
    rules: '',
  });

  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const response = await apiClient.createHackday(formData);

      if (response.success) {
        // Invalidate related caches
        apiClient.invalidateCache('hackdays');
        apiClient.invalidateCache('sponsor-next-event');
        apiClient.invalidateCache('currentprojects');

        // Show success message
        alert('Event created successfully!');

        // Navigate to sponsor dashboard
        navigate('/sponsor/dashboard');
      } else {
        console.error('Failed to create event:', response.error);
        alert('Failed to create event: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating event:', error);
      alert('An error occurred while creating the event');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All changes will be lost.')) {
      navigate('/sponsor/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50/50 pt-20 sm:pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/sponsor/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create New Event</h1>
          <p className="text-gray-600">Fill in the details below to create a new hackday event.</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-purple-200 overflow-hidden">
          <form onSubmit={handleSubmit} className="p-6 sm:p-8">
            {/* Basic Information Section */}
            <div className="edit-event-form-section mb-8">
              <h3 className="edit-event-section-title">
                <FileText className="h-5 w-5" />
                Basic Information
              </h3>

              <div className="edit-event-form-grid">
                <div className="edit-event-form-group edit-event-form-full">
                  <label className="edit-event-form-label">
                    Event Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    className="edit-event-form-input"
                    placeholder="e.g. AI Innovation Hackathon 2024"
                  />
                </div>

                <div className="edit-event-form-group edit-event-form-full">
                  <label className="edit-event-form-label">
                    Description
                  </label>
                  <MarkdownEditor
                    value={formData.description}
                    onChange={(value) => handleChange('description', value)}
                    placeholder="Describe what participants will be building, the goals of the hackathon, and what makes it special. Use **bold**, *italic*, [links](url), and - bullet lists."
                    height={200}
                  />
                </div>

                <div className="edit-event-form-group">
                  <label className="edit-event-form-label">
                    <Tag className="h-4 w-4 inline mr-1" />
                    Theme
                  </label>
                  <input
                    type="text"
                    value={formData.theme}
                    onChange={(e) => handleChange('theme', e.target.value)}
                    className="edit-event-form-input"
                    placeholder="e.g. AI for Social Good"
                  />
                </div>

                <div className="edit-event-form-group">
                  <label className="edit-event-form-label">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    className="edit-event-form-select"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Dates Section */}
            <div className="edit-event-form-section mb-8">
              <h3 className="edit-event-section-title">
                <Calendar className="h-5 w-5" />
                Dates & Deadlines
              </h3>

              <div className="edit-event-form-grid">
                <div className="edit-event-form-group">
                  <label className="edit-event-form-label">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => handleChange('startDate', e.target.value)}
                    className="edit-event-form-input"
                  />
                </div>

                <div className="edit-event-form-group">
                  <label className="edit-event-form-label">
                    End Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => handleChange('endDate', e.target.value)}
                    className="edit-event-form-input"
                  />
                </div>

                <div className="edit-event-form-group">
                  <label className="edit-event-form-label">
                    Registration Deadline
                  </label>
                  <input
                    type="date"
                    value={formData.registrationDeadline}
                    onChange={(e) => handleChange('registrationDeadline', e.target.value)}
                    className="edit-event-form-input"
                  />
                </div>
              </div>
            </div>

            {/* Participants & Settings Section */}
            <div className="edit-event-form-section mb-8">
              <h3 className="edit-event-section-title">
                <Users className="h-5 w-5" />
                Participants & Settings
              </h3>

              <div className="edit-event-form-grid">
                <div className="edit-event-form-group">
                  <label className="edit-event-form-label">
                    Max Participants
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.maxParticipants}
                    onChange={(e) => handleChange('maxParticipants', parseInt(e.target.value) || 0)}
                    className="edit-event-form-input"
                  />
                </div>

                <div className="edit-event-form-group">
                  <label className="edit-event-form-label">
                    Event Link (Optional)
                  </label>
                  <input
                    type="url"
                    value={formData.eventLink}
                    onChange={(e) => handleChange('eventLink', e.target.value)}
                    className="edit-event-form-input"
                    placeholder="https://..."
                  />
                </div>

                <div className="edit-event-form-group edit-event-form-full">
                  <label className="edit-event-form-label">
                    Rules & Guidelines (Optional)
                  </label>
                  <textarea
                    rows={3}
                    value={formData.rules}
                    onChange={(e) => handleChange('rules', e.target.value)}
                    className="edit-event-form-textarea"
                    placeholder="List any rules, requirements, or guidelines for participants..."
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-6">
              <button
                type="button"
                onClick={handleCancel}
                className="edit-event-btn edit-event-btn-secondary"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="edit-event-btn edit-event-btn-primary"
                disabled={isSaving}
              >
                {isSaving ? (
                  <span className="edit-event-btn-loading">
                    <div className="edit-event-spinner"></div>
                    Creating...
                  </span>
                ) : (
                  <span className="edit-event-btn-content">
                    <Plus className="h-4 w-4" />
                    Create Event
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateEvent;
